import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Divider } from 'antd';
import { ShoppingCartOutlined } from '@ant-design/icons';
import cx from 'classnames';
import { ITariffToBuyListItem, ITariffToBuyListPaginated } from '../../interfaces';
import { useProfile, useRouterStore, useUiIsSpinnerFound } from '../../hooks';
import { getCompaniesChildrenSelect, getTariffToBuyList } from '../../middleware';
import { getTariffPackagesList } from '../../middleware';
import { tI, tNM } from '../../translate';
import { Catcher, InfiniteScroll, LoadingApiByIds } from '../../Components';
import { TariffCard } from './Components';
import CardStyles from '../styles/m_itemCardStyles.less';
import TariffsStyles from './m_styles.less';

export const TariffToBuyList: React.FC = observer(() => {
    const companyId = useProfile().companyId;
    const routerStore = useRouterStore();
    const [page, setPage] = useState(1);
    const [tariffs, setTariffs] = useState<ITariffToBuyListItem[]>([]);
    const [total, setTotal] = useState(0);
    const pageSize = 10;
    const isLoading = useUiIsSpinnerFound(['GET_TARIFFS_TO_BUY']);

    const goTo = (event: React.MouseEvent<HTMLDivElement>, id: string | undefined): void => {
        if (!(event.ctrlKey || event.metaKey || event.altKey || event.shiftKey)) {
            event.preventDefault();
            routerStore.goTo('TariffToBuyItem', { params: { id } }).then();
        }
    };

    const fetchData = async (page: number, reset?: boolean) => {
        const resp: ITariffToBuyListPaginated = (await getTariffToBuyList(
            { pageNumber: page, pageSize: pageSize },
            true,
        )) as ITariffToBuyListPaginated;

        const { list, meta } = resp;
        const { records_count } = meta;

        if (reset) {
            setTariffs(list);
        } else {
            setTariffs((prevList) => [...prevList, ...list]);
        }

        setTotal(records_count || 0);
    };

    useEffect(() => {
        fetchData(page).then();
        getTariffPackagesList().then();

        if (companyId) {
            getCompaniesChildrenSelect(companyId || '', true).then();
        }
    }, []);

    return (
        <Catcher>
            <div className={cx(CardStyles.main, TariffsStyles.container)}>
                <div className={TariffsStyles.tariffItemContainer}>
                    <div className={TariffsStyles.tariffItemHeaderContainer}>
                        <div>
                            <div className={TariffsStyles.header}>
                                <ShoppingCartOutlined className={TariffsStyles.icon} />
                                {tI('Tariffs to Buy')}
                            </div>
                            <p>{tI('Browse and purchase available tariff plans')}</p>
                        </div>
                    </div>
                    <Divider />
                    {tariffs.length === 0 && !isLoading && (
                        <div className={TariffsStyles.notFound}>{tNM('No tariffs available')}</div>
                    )}
                    <InfiniteScroll
                        pageSize={pageSize}
                        page={page}
                        fetchFunc={fetchData}
                        total={total}
                        current={tariffs.length}
                        handleSetPage={setPage}
                    >
                        <div className={TariffsStyles.typeSelector}>
                            {tariffs.map((item) => (
                                <TariffCard
                                    {...item}
                                    id={item.id.toString()}
                                    onClick={(e) => goTo(e, item.id.toString())}
                                    key={item.id.toString()}
                                    toBuy={true}
                                    cardsCount={item.cardsCount}
                                />
                            ))}
                        </div>
                    </InfiniteScroll>
                </div>
                <LoadingApiByIds idSpinners={['GET_TARIFFS_TO_BUY']} />
            </div>
        </Catcher>
    );
});
